import React, { useState } from 'react';
import { FormConfiguration, FormSubmissionData } from '../../models/forms/form-models';
import { EnrollmentType } from '../../models/enrollment/enrollment';
import { DynamicFormField } from './DynamicFormField';
import { enrollmentService } from '../../services/enrollment-service';
import { useToast, LoadingSpinner } from './';

interface DynamicFormProps {
  formConfig: FormConfiguration;
  enrollmentType: EnrollmentType;
  entityId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
  isRTL?: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  formConfig,
  enrollmentType,
  entityId,
  onSuccess,
  onCancel,
  isRTL = false
}) => {
  const [formData, setFormData] = useState<FormSubmissionData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  // Initialize form data with default values
  React.useEffect(() => {
    const initialData: FormSubmissionData = {};
    formConfig.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          initialData[field.name] = field.defaultValue;
        }
      });
    });
    setFormData(initialData);
  }, [formConfig]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await enrollmentService.submitEnrollment(
        enrollmentType,
        entityId,
        formData,
        formConfig
      );

      if (result.success) {
        addToast({
          type: 'success',
          title: 'Enrollment Successful!',
          message: result.message,
          duration: 6000
        });

        onSuccess?.();
      } else {
        if (result.validationErrors) {
          setErrors(result.validationErrors);
        }

        addToast({
          type: 'error',
          title: 'Enrollment Failed',
          message: result.error || 'Please correct the errors and try again',
          duration: 8000
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Connection Error',
        message: 'Unable to submit enrollment. Please check your connection and try again.',
        duration: 8000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sort sections by order
  const sortedSections = [...formConfig.sections].sort((a, b) => a.order - b.order);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Form Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {formConfig.title}
        </h2>
        {formConfig.description && (
          <p className="text-gray-600 leading-relaxed">
            {formConfig.description}
          </p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {sortedSections.map((section) => (
          <div key={section.id} className="space-y-6">
            {/* Section Header */}
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {section.title}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  {section.description}
                </p>
              )}
            </div>

            {/* Section Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {section.fields.map((field) => {
                // Check conditional rendering
                if (field.conditional) {
                  const dependentValue = formData[field.conditional.dependsOn];
                  if (dependentValue !== field.conditional.showWhen) {
                    return null;
                  }
                }

                // Determine field span
                const isFullWidth =
                  field.type === 'textarea' ||
                  field.type === 'multi-select' ||
                  field.type === 'radio' ||
                  field.type === 'checkbox';

                return (
                  <div
                    key={field.id}
                    className={isFullWidth ? 'md:col-span-2' : ''}
                  >
                    <DynamicFormField
                      field={field}
                      value={formData[field.name]}
                      error={errors[field.name]}
                      onChange={handleFieldChange}
                      disabled={isSubmitting}
                      isRTL={isRTL}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Form Actions */}
        <div className={`flex flex-col sm:flex-row space-y-3 sm:space-y-0 ${isRTL ? 'sm:space-x-reverse' : ''} sm:space-x-4 pt-6 border-t border-gray-200`}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1 bg-gray-100 text-gray-800 hover:bg-gray-200 py-4 px-6 rounded-lg transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formConfig.cancelButtonText || 'Cancel'}
            </button>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-black text-white hover:bg-gray-800 py-4 px-6 rounded-lg transition-colors font-semibold text-lg shadow-lg disabled:opacity-75 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                <span>Submitting...</span>
              </>
            ) : (
              <span>{formConfig.submitButtonText}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
